/**
 * 完了画面のログインポップアップの計測（表示・放棄・ログイン成功）。
 *
 * 放棄はダイアログの明示的なクローズだけでなく、document が hidden になった時点でも
 * 計上する。以前はクローズだけを見ていたため、タブを閉じる・別アプリへ移るといった
 * 離脱がまるごと落ちていて、放棄率は下限値でしかなかった。
 *
 * hidden は「戻ってくるタブ切替」やリダイレクト系ログインの遷移でも起きるので、
 * その後に認証が成立したら login_popup_abandon_recovered を送って打ち消す。
 * 集計側では abandon − abandon_recovered が真の放棄になる。
 */
import { useEventListener } from '@vueuse/core'
import localforage from 'localforage'
import { useAuth } from '@/composables/useAuth'
import {
  clearLoginPopupAbandoned,
  consumeLoginPopupAbandoned,
  LOGIN_POPUP_ABANDON_REASON,
  LOGIN_POPUP_REDIRECT_KEY,
  markLoginPopupAbandoned,
  type LoginPopupAbandonReason,
  type LoginPopupRedirectRecord
} from '@/utils/login-popup-tracking'

type LoginPopupGtm = {
  sendLoginPopupEvent: (
    eventName: string,
    params?: Record<string, unknown>
  ) => void
}

/** 計測中の1試行。ポップアップを開いてから、成功か明示クローズまで生きる。 */
type Attempt = {
  triggerButton: string
  formatId: string
  /** この試行で放棄を既に計上したか。hidden/show の往復で二重計上しないための番。 */
  abandonReported: boolean
}

export function useLoginPopupTracking(gtm: LoginPopupGtm) {
  const { isAuthenticated } = useAuth()

  // 画面に出す値ではなく手続き上の状態なので ref にはしない。
  let attempt: Attempt | null = null

  /** ポップアップを開いた。ここから1試行が始まる。 */
  function trackOpen(triggerButton: string, formatId: string): void {
    attempt = { triggerButton, formatId, abandonReported: false }
    // 前の試行の放棄を新しい試行の成功で打ち消してしまわないよう、先に捨てる。
    clearLoginPopupAbandoned()
    gtm.sendLoginPopupEvent('open', {
      trigger_button: triggerButton,
      format_id: formatId,
      // GTM の dataLayer はキーが後続の push に残る（{{DLV - abandon_reason}} は前回の値を
      // 返す）。明示的に空へ倒さないと、放棄の理由が open / login_success に付いて誤読を生む。
      abandon_reason: ''
    })
    // リダイレクトで一度ページを離れても成功判定できるよう退避する。
    const record: LoginPopupRedirectRecord = {
      triggerButton,
      formatId,
      ts: Date.now()
    }
    localforage.setItem(LOGIN_POPUP_REDIRECT_KEY, record)
  }

  /**
   * 放棄を計上する。1試行につき1回まで。
   *
   * dialogClose は試行を終了させる（明示的に閉じたので打ち消しは用意しない）。
   * pageHidden は試行を残し、戻ってきてログインしたときに打ち消せるようにする。
   */
  function trackAbandon(reason: LoginPopupAbandonReason): void {
    if (!attempt || isAuthenticated.value) {
      return
    }
    const current = attempt
    if (!current.abandonReported) {
      current.abandonReported = true
      gtm.sendLoginPopupEvent('abandon', {
        trigger_button: current.triggerButton,
        format_id: current.formatId,
        abandon_reason: reason
      })
      if (reason === LOGIN_POPUP_ABANDON_REASON.pageHidden) {
        markLoginPopupAbandoned(reason)
      }
    }
    if (reason === LOGIN_POPUP_ABANDON_REASON.dialogClose) {
      attempt = null
      localforage.removeItem(LOGIN_POPUP_REDIRECT_KEY)
    }
  }

  /**
   * 同一ページで認証が成立した。放棄を計上済みなら打ち消しも送る。
   * リダイレクト経由の成功はページ再読み込みで attempt が失われているため
   * ここには来ない（authLifecycle.ts の consumePendingLoginPopupRedirect が送る）。
   */
  function trackLoginSuccess(): void {
    if (!attempt) {
      return
    }
    const current = attempt
    attempt = null
    localforage.removeItem(LOGIN_POPUP_REDIRECT_KEY)
    gtm.sendLoginPopupEvent('login_success', {
      trigger_button: current.triggerButton,
      format_id: current.formatId,
      // open と同じ理由で、直前の放棄の理由を引きずらないよう空へ倒す。
      abandon_reason: ''
    })
    const recoveredFrom = consumeLoginPopupAbandoned()
    if (recoveredFrom) {
      gtm.sendLoginPopupEvent('abandon_recovered', {
        trigger_button: current.triggerButton,
        format_id: current.formatId,
        abandon_reason: recoveredFrom
      })
    }
  }

  const reportHidden = () => trackAbandon(LOGIN_POPUP_ABANDON_REASON.pageHidden)

  // visibilitychange はタブ切替・バックグラウンド化を拾う。
  // pagehide は別ドキュメントへの遷移（リダイレクト系ログイン）で visibilitychange が
  // 発火しない環境の保険。二重計上は abandonReported が防ぐ。
  useEventListener(document, 'visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      reportHidden()
    }
  })
  useEventListener(window, 'pagehide', reportHidden)

  return { trackOpen, trackAbandon, trackLoginSuccess }
}
